import type { PublicKeyCredentialCreationOptionsJSON, RegistrationResponseJSON } from "@simplewebauthn/browser";
import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { DLTInfo, DLTProtocolEnum, TransactionSigner, WalletDLT, WalletNonces } from "@framework/wallet-custody-domain/bin/blockchain.js";
import { AttributeValueType, passwordLessIndicator, WalletAuthenticationActivity, WalletCustodyApi, WalletPublicInfo } from "@framework/wallet-custody-domain/bin/wallet-custody.js";
import { handleError } from "./common.js";
import { Mutex } from "./mutex.js";
export * from "./monitoring.js"
export type { DLTInfo, TransactionSigner, WalletDLT};
export { DLTProtocolEnum, WalletNonces};

export interface Lockable {
  unlock(): Promise<boolean>;
  get locked(): boolean;
}

function base64ToUtf8Json<T = any>(base64: string): T {
  const binary = atob(base64)
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0))
  const json = new TextDecoder('utf-8').decode(bytes)
  return JSON.parse(json)
}

function validateToken(token:string|undefined, forAddress: string, marginSec: number = 0): boolean {
  // token is expected to be a JWT, but lets not use a library
  if (!token) return false
  const [header, body, signature] = token.split('.');
  // log.debug("validateToken", header, body, signature);
  if (!signature) return false;
  if (!header) return false;
  if (!body) return false;

  try {
    let payload = base64ToUtf8Json(body);
    // The payload has a base64 header and payload i the challenge field if the token is signed locally

    // log.debug("validateToken", payload);
    if (!payload.issuer) return false;
    // commented as it compromize the new functionality of authenticating with the master wallet
    // if (payload.issuer.toLowerCase() !== forAddress.toLowerCase()) return false;
    if (!payload.exp) return false;
    // verify that the token has not expired or will not expire within the next marginSec
    // as we need to account for the communication delay between the caller and the back end service 
    //
    // console.log("validateToken", (payload.exp - marginSec) >= Math.ceil(Date.now() / 1000), payload.exp, Math.ceil(Date.now() / 1000), marginSec, );
    
    if ( (payload.exp - marginSec) < Math.ceil(Date.now() / 1000) ) return false;
    return true;
  } catch (error) {
    return false;
  }
}

// WalletCustodyApiImpl.sign() returns the signed tx as a hex string (see its
// own "should not exists in v2" comment) - decode it back to the Buffer the
// TransactionSigner interface contract promises.
function hexStringToBuffer(v: string): Buffer {
  if (v.startsWith('0x')) v = v.slice(2);
  return Buffer.from(v, 'hex');
}

class LockableAbstract implements Lockable {
  protected currentToken: string|undefined;
  protected mutex = new Mutex();
  constructor(protected address: string) {}
  protected async _authenticate(activities: WalletAuthenticationActivity[]): Promise<string|undefined> {
    throw new Error("Not implemented");
  }
  
  async unlock(): Promise<boolean> {
    // First check if token is valid since validateToken is synchronous
    if (validateToken(this.currentToken, this.address, 2 /* 2 second for processing + communication delay */)) return true;
    
    // Token is invalid, need to authenticate
    // Wait if another thread is already authenticating
    return this.mutex.withLock(async () => {    
      // Check if token is valid again after waiting, as another thread may have refreshed it
      if (validateToken(this.currentToken, this.address, 2 /* 2 second for processing + communication delay */)) return true;
      
      const token = await this._authenticate([]);
      if (token) {
        this.currentToken = token;
        return true;
      } else {
        return false;
      }
    });
  }
  
  get locked(): boolean {
    return !validateToken(this.currentToken, this.address, 1 /* 1 second for communication delay */);
  }
}
class WalletCustodyTransactionSignerImpl extends LockableAbstract implements TransactionSigner, Lockable {
  constructor(private api: WalletCustodyApi, address: string, private password: string) {
    super(address);
  }
  _authenticate(activities: WalletAuthenticationActivity[]): Promise<string|undefined> {
    return this.api.authenticate(this.address, this.password, activities);
  }
  // Forced modification of the api to replace Buffer by hex string
  async sign(w: WalletDLT, tx: any): Promise<any> {
    //const token = await this.api.authenticate(w.getAddress(), this.password);
    if (this.locked) throw new Error("You should unlock the signer before signing");
    try {
      const info = await w.getInfo();
      let signedTx = await this.api.sign(this.address, this.currentToken || '', tx, info);
      return hexStringToBuffer(signedTx);
    } catch (error) {
      if (/token expired/.test((error as any).message)) {
        this.currentToken = undefined; // force clearing the token since the server didn't like it
      }
      throw error;
    }
  }
  
}

export type CustodyAuthenticatorFunction = (address: string, api: WalletCustodyApi) => Promise<string|undefined>;
class WalletCustodyTransactionSignerWithCallbackImpl extends LockableAbstract implements TransactionSigner, Lockable {
  constructor(private api: WalletCustodyApi, address: string, private authenticate: CustodyAuthenticatorFunction) {
    super(address);
  }
  _authenticate(): Promise<string|undefined> {
    return this.authenticate(this.address, this.api);
  }
  
  // Forced modification of the api to replace Buffer by hex string
  async sign(w: WalletDLT, tx: any): Promise<any> {

    //const token = await this.authenticate(w.getAddress(), this.api);
    //if (!token) throw new Error("Failed to authenticate the wallet");
    if (this.locked)
      throw new Error("You should unlock the signer before signing");
    try {
      const info = await w.getInfo();
      let signedTx = await this.api.sign(w.getAddress(), this.currentToken || '', tx, info);
      return hexStringToBuffer(signedTx);
    } catch (error) {
      if (/token expired/.test((error as any).message)) {
        this.currentToken = undefined; // force clearing the token since the server didn't like it
      }
      throw error;
    }
  }
}

export class WalletCustodyApiImpl implements WalletCustodyApi {
  private http: AxiosInstance;
  constructor(
    protected baseUrl: string,
    protected chain: DLTProtocolEnum,
    // Optional Node-only connection-reuse agents (see #479/#505) - this package also
    // ships a browser build (wallet-custody/front), so it must never import
    // node:http/node:https itself; Node callers construct and pass their own.
    httpAgents?: Pick<AxiosRequestConfig, "httpAgent" | "httpsAgent">
  ) {
    this.http = axios.create({
      baseURL: new URL(this.baseUrl).href,
      responseType: "json",
      validateStatus: (status) => status >= 200 && status <= 399,
      ...httpAgents,
    });
  }

  getSigner(address: string, auth: string|CustodyAuthenticatorFunction): TransactionSigner {
    if (typeof auth === "string") {
      return new WalletCustodyTransactionSignerImpl(this, address, auth);
    } else {
      return new WalletCustodyTransactionSignerWithCallbackImpl(this, address, auth);
    }
  }

  get protocol(): DLTProtocolEnum {
    return this.chain;
  }
  
  async newWallet(password: string, backupAddress?: string): Promise<string> {
    try {
      const res = await this.http.post(`${this.chain}/wallet/new`,{
        password,
        backupAddress
      })
      return res.data.address;
    } catch (error) {
      handleError(error, "fail creating a new wallet");
    }
  }
  
  async newWebauthnWalletWithAttributes(attributes: Record<string, AttributeValueType>, backupAddress?: string): Promise<PublicKeyCredentialCreationOptionsJSON> {
    try {
      const res = await this.http.post(`${this.chain}/wallet/new`,{
        password: passwordLessIndicator,
        backupAddress,
        attributes
      })
      return res.data;
    } catch (error) {
      handleError(error, "fail creating a new passwordless wallet");
    }
  }

  async registerWebauthn(address: string, authenticator: RegistrationResponseJSON): Promise<string> {
    try {
      const res = await this.http.post(`${this.chain}/wallet/${address}/webauthn-registrer`, {
        ...authenticator
      })
      return res.data;
    } catch (error) {
      handleError(error, "fail creating a new passwordless wallet");
    }
  }

  async newWalletWithAttributes(password: string, attributes: Record<string, AttributeValueType>, backupAddress?: string): Promise<string> {
    try {
      const res = await this.http.post(`${this.chain}/wallet/new`,{
        password,
        backupAddress,
        attributes
      })
      return res.data.address;
    } catch (error) {
      handleError(error, "fail creating a new wallet with attributes");
    }
  }

  async publicInfo(address: string): Promise<WalletPublicInfo  & {exists: boolean}> {
    try {
      const res = await this.http.get(`${this.chain}/wallet/${address}`);
      return {
        address: res.data.address,
        chain: res.data.chain,
        dateCreation: res.data.dateCreation,
        publicKey: res.data.publicKey,
        exists: res.data.exists,
        backupAddress: res.data.backupAddress,
        attributes: res.data.attributes || {}
      }
    } catch (error) {
      handleError(error, "fail getting the wallet public info")
    }
  }
  async findByAttribute<T extends AttributeValueType>(attribute: string, value: T): Promise<WalletPublicInfo[]> {
    try {
      const res = await this.http.post(`${this.chain}/wallet/attribute`, {
        attribute,
        value
      });
      return res.data;
    } catch (error) {
      handleError(error, "fail finding wallets by attribute")
    }
  }  

  private async createTokenFromWebauthn(address:string, tokenOptions: string): Promise<string> {
    try {
      // @simplewebauthn/browser is a pure-ESM-only package; load it dynamically here (instead of a
      // static top-level import) so that this module's CJS build doesn't fail to `require()` it at
      // load time for Node.js consumers that never call this webauthn-specific method.
      const { base64URLStringToBuffer, bufferToBase64URLString, startAuthentication } = await import("@simplewebauthn/browser");
      // interpret the tokenOptions as a webauthn authentification config
      const decoder = new TextDecoder("utf-8");
      const encoder = new TextEncoder();
      const config = JSON.parse(decoder.decode(base64URLStringToBuffer(tokenOptions)));
      // console.log("authConfig", config);

      // call the webauthn authenticator in the user's device
      const authresp = await startAuthentication({ optionsJSON: config });

      // We need to construct a toke that is compatible with validateToken()
      // it reaquire a 3 part jwt token with a header, a payload and a signature
      // the payload should contain the issuer, exp to test the token validity
      // The header should contain the type of the token at least to differentiate easilly from the password based

      console.log("authresp", authresp);
      // extract the content of the clientDataJSON base64URL encoded string. Attention, we need to keep this clientDataJSON intact because it will be hashed to verify the signature
      const data = JSON.parse(decoder.decode(base64URLStringToBuffer(authresp.response.clientDataJSON))) as {challenge: string, origin: string, type: string};
      // decode our chalenge data that contains what we normally want in the token
      let {header, payload} = JSON.parse(decoder.decode(base64URLStringToBuffer(data.challenge))) as {header: any, payload: any};

      // console.log("data", data, header, payload, authresp.response.signature);
      header = {
        type: header.type, // "webauthn"
        typ: authresp.type,
        id: authresp.id,
        auth: authresp.response.authenticatorData, // needed to verify the signature
      }
      payload = {
        issuer: payload.issuer || address,
        exp: payload.exp,
        data: authresp.response.clientDataJSON // contains the rest we need 
      }
      const headerB64 = bufferToBase64URLString(encoder.encode(JSON.stringify(header)).buffer);
      const payloadB64 = bufferToBase64URLString(encoder.encode(JSON.stringify(payload)).buffer);
      return `${headerB64}.${payloadB64}.${authresp.response.signature}`;
    } catch (error) {
      handleError(error, "fail to create a token from webauthn")
    }
  }

  async authenticate(address: string, password: string, activities?:WalletAuthenticationActivity[]): Promise<string> {
    try {
      const res = await this.http.post(`${this.chain}/wallet/${address}/auth`, {
        password,
        activities // when not provided or undefined will be handled as `sign` 
      });
      // see the form of the token 
      const nbParts = res.data.token.split('.').length;
      if (nbParts !== 3) {
        return await this.createTokenFromWebauthn(address, res.data.token);
      } else {
        return res.data.token;
      }

    } catch (error) {
      handleError(error, "authentication failed")
    }
  }

  async sign(address: string, token: string, rawTx: string, dltInfo: DLTInfo): Promise<string> {
    try {
      let raw: string;
      if (typeof rawTx === "string") {
        if (rawTx.startsWith('0x')) rawTx = rawTx.substr(2);
        raw = rawTx;
      } else raw = (rawTx as Buffer).toString("hex"); // should not exists in v2.
      // log.debug("Sending to sign", "0x" + raw.toString("hex"));
      const res = await this.http.post(`${this.chain}/wallet/${address}/sign`, {
        raw: '0x'+raw,
        token,
        dlt: dltInfo
      });
      return res.data.signedTx;
    } catch (error) {
      handleError(error, "fail to sign transaction")
    }
  }

  async delete(address: string, token: string): Promise<string> {
    try {
      const res = await this.http.delete(`${this.chain}/wallet/${address}`, {
        headers: { authorization: 'bearer '+token}
      });
      return res.data.deleted;
    } catch (error) {
      handleError(error, "fail to delete wallet")
    }
  }

  async updatePassword(address: string, newPassword: string, token: string): Promise<string> {
    try {
      const res = await this.http.post(`${this.chain}/wallet/${address}/update-password`, {
        newPassword
      }, {
        headers: { authorization: 'bearer '+token}
      });
      return res.data;
    } catch (error) {
      handleError(error, "fail to update wallet password")
    }
  }

  async updateAttributes(address: string, updated: Record<string, AttributeValueType>, deleted: string[], token: string): Promise<WalletPublicInfo> {
    try {
      
      const res = await this.http.post(`${this.chain}/wallet/${address}/update-attributes`, {
        updated,
        deleted
      }, {
        headers: {
          authorization: 'bearer '+token
        }
      });
      return res.data;
    } catch (error) {
      handleError(error, "fail to update wallet attributes")
    }
  }

}
